import React, { useEffect, useMemo, useState } from 'react';
import toast from '../utils/toast';
import { api } from '../config/api';
import { CalendarDays, Plus, ArrowLeft, Save, Loader2, Edit, Trash2, CheckCircle2, XCircle,Search } from 'lucide-react';
import { formatDisplayDate, toDateInputValue } from '../utils/dateFormat';
import SearchBar from '../components/SearchBar';
import {
  masterAccentButton,
  masterHeader,
  masterHeaderCard,
  masterHeaderIcon,
  masterInput,
  masterInputLabel,
  masterPageShell,
  masterPanel,
  masterPanelPadding,
  masterPrimaryButton,
  masterSelect,
  masterStickyHeadRow,
  masterTitle,
  masterTableID,
  masterTableData,
   masterToolbar, masterSearchWrap, masterSearchInput
} from '../components/masterPageStyles';

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const todayYmd = toDateInputValue(new Date());

// Helper: Convert 24h string ("17:00") to 12h format ("05:00 PM")
const formatTo12Hour = (time24) => {
  if (!time24 || !time24.includes(':')) return time24 || '';
  let [hours, minutes] = time24.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')} ${period}`;
};

// Helper: Convert 12h string ("05:00 PM" or "05.00 pm") back to 24h ("17:00") for HTML time inputs
const formatTo24Hour = (time12) => {
  if (!time12) return '';
  const match = time12.trim().match(/^(\d{1,2})[:.](\d{2})\s*(AM|PM)$/i);
  if (!match) return time12;
  
  let [, hours, minutes, period] = match;
  let h = parseInt(hours, 10);
  if (period.toUpperCase() === 'PM' && h < 12) h += 12;
  if (period.toUpperCase() === 'AM' && h === 12) h = 0;
  
  return `${String(h).padStart(2, '0')}:${minutes}`;
};

const toMinutes = (timeValue) => {
  if (!timeValue) return null;
  const time24 = formatTo24Hour(timeValue);
  if (!time24 || !time24.includes(':')) return null;
  const [hours, minutes] = time24.split(':').map(Number);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;
  return (hours * 60) + minutes;
};

const getCalculatedMaxPatients = ({ startTime, endTime, slotDuration, breakStartTime, breakEndTime }) => {
  const startMinutes = toMinutes(startTime);
  const endMinutes = toMinutes(endTime);
  const breakStartMinutes = toMinutes(breakStartTime);
  const breakEndMinutes = toMinutes(breakEndTime);
  const slotMinutes = Number(slotDuration);

  if (startMinutes === null || endMinutes === null || !Number.isFinite(slotMinutes) || slotMinutes <= 0) return '';

  let availableMinutes = endMinutes - startMinutes;
  if (availableMinutes <= 0) return '';

  if (breakStartMinutes !== null && breakEndMinutes !== null) {
    if (breakEndMinutes <= breakStartMinutes) return '';
    const boundedBreakStart = Math.max(breakStartMinutes, startMinutes);
    const boundedBreakEnd = Math.min(breakEndMinutes, endMinutes);
    const breakMinutes = Math.max(boundedBreakEnd - boundedBreakStart, 0);
    availableMinutes -= breakMinutes;
  }

  if (availableMinutes <= 0) return '0';
  return String(Math.floor(availableMinutes / slotMinutes));
};

const getInitialWeeklyTemplate = () => {
  return DAYS.reduce((acc, day) => {
    acc[day] = {
      isActive: false,
      shifts: []
    };
    return acc;
  }, {});
};

const getInitialFormData = () => ({
  scheduleId: '',
  doctorId: '',
  doctorName: '',
  startDate: todayYmd,
  endDate: todayYmd,
  weeklyTemplate: getInitialWeeklyTemplate()
});

const DoctoreSchedule = () => {
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [doctors, setDoctors] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState(getInitialFormData());
  const [currentPage, setCurrentPage] = useState(1);
 const [pageSize, setPageSize] = useState(10); // Changed from static value to useState with setter

  const fetchDoctors = async () => {
    try {
      const res = await api.get(`/doctors/all`);
      if (res.data.success) setDoctors(res.data.data || []);
    } catch (err) {
      toast.error('FAILED TO FETCH DOCTOR LIST');
    }
  };

  const fetchSchedules = async () => {
    try {
      const res = await api.get(`/doctor-schedules/all`);
      if (res.data.success) setSchedules(res.data.data || []);
    } catch (err) {
      toast.error('FAILED TO FETCH SCHEDULES');
    }
  };

  // Fixed: Removed the accidental 'dayConfig' and 'idx' parameters here
  const fetchNextScheduleId = async () => {
    try {
      const res = await api.get(`/doctor-schedules/next-id`);
      if (res.data.success) {
        setFormData((prev) => ({ ...prev, scheduleId: res.data.nextId }));
      }
    } catch (err) {
      toast.error('FAILED TO GENERATE ID');
    }
  };

  useEffect(() => {
    fetchDoctors();
    fetchSchedules();
  }, []);

  const handleToggleFormView = () => {
    if (!showForm) {
      setEditingId(null);
      setFormData(getInitialFormData());
      fetchNextScheduleId();
      setShowForm(true);
    } else {
      setShowForm(false);
    }
  };

  const handleBaseInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'doctorId') {
      const selectedDoctor = doctors.find((doc) => doc.doctorId === value);
      setFormData((prev) => ({
        ...prev,
        doctorId: selectedDoctor?.doctorId || '',
        doctorName: selectedDoctor?.doctorName || ''
      }));
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const toggleDayActive = (day) => {
    setFormData((prev) => {
      const nextActive = !prev.weeklyTemplate[day].isActive;
      
      return {
        ...prev,
        weeklyTemplate: {
          ...prev.weeklyTemplate,
          [day]: {
            isActive: nextActive,
            shifts: nextActive ? [{
              startTime: '',
              endTime: '',
              slotDuration: '15',
              breakStartTime: '',
              breakEndTime: '',
              bookingMode: 'Slot Based',
              maxPatientsPerDay: ''
            }] : []
          }
        }
      };
    });
  };

  const handleShiftFieldChange = (day, shiftIndex, field, value) => {
    setFormData((prev) => {
      const updatedShifts = prev.weeklyTemplate[day].shifts.map((shift, idx) => {
        if (idx !== shiftIndex) return shift;
        
        const updatedShift = { ...shift, [field]: value };
        if (['startTime', 'endTime', 'slotDuration', 'breakStartTime', 'breakEndTime'].includes(field)) {
          updatedShift.maxPatientsPerDay = getCalculatedMaxPatients(updatedShift);
        }
        return updatedShift;
      });

      return {
        ...prev,
        weeklyTemplate: {
          ...prev.weeklyTemplate,
          [day]: {
            ...prev.weeklyTemplate[day],
            shifts: updatedShifts
          }
        }
      };
    });
  };

  const addShiftToDay = (day) => {
    setFormData((prev) => {
      const currentShifts = prev.weeklyTemplate[day].shifts;
      const newShiftItem = {
        startTime: '',
        endTime: '',
        slotDuration: '15',
        breakStartTime: '',
        breakEndTime: '',
        bookingMode: 'Slot Based',
        maxPatientsPerDay: ''
      };

      return {
        ...prev,
        weeklyTemplate: {
          ...prev.weeklyTemplate,
          [day]: {
            ...prev.weeklyTemplate[day],
            shifts: currentShifts.concat(newShiftItem)
          }
        }
      };
    });
  };

  const removeShiftFromDay = (day, shiftIndex) => {
    setFormData((prev) => {
      const filteredShifts = prev.weeklyTemplate[day].shifts.filter((_, idx) => idx !== shiftIndex);
      return {
        ...prev,
        weeklyTemplate: {
          ...prev.weeklyTemplate,
          [day]: {
            isActive: filteredShifts.length > 0,
            shifts: filteredShifts
          }
        }
      };
    });
  };

  const handleEdit = (item) => {
    setEditingId(item._id);

    const convertedTemplate = JSON.parse(JSON.stringify(item.weeklyTemplate || getInitialWeeklyTemplate()));
    DAYS.forEach((day) => {
      if (convertedTemplate[day]?.shifts) {
        convertedTemplate[day].shifts = convertedTemplate[day].shifts.map((s) => ({
          ...s,
          startTime: formatTo24Hour(s.startTime),
          endTime: formatTo24Hour(s.endTime),
          breakStartTime: formatTo24Hour(s.breakStartTime),
          breakEndTime: formatTo24Hour(s.breakEndTime)
        }));
      }
    });

    setFormData({
      scheduleId: item.scheduleId || '',
      doctorId: item.doctorId || '',
      doctorName: item.doctorName || '',
      startDate: item.startDate ? item.startDate.split('T')[0] : todayYmd,
      endDate: item.endDate ? item.endDate.split('T')[0] : todayYmd,
      weeklyTemplate: convertedTemplate
    });
    setShowForm(true);
  };

  const handleDelete = async (item) => {
    const ok = window.confirm(`Delete entire rotational schedule loop rule for ${item.doctorName}?`);
    if (!ok) return;
    try {
      const res = await api.delete(`/doctor-schedules/${item._id}`);
      if (res.data.success) {
        toast.success('SCHEDULE DELETED');
        fetchSchedules();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'DELETE OPERATION ABORTED');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.doctorId) return toast.error('PLEASE SELECT DOCTOR');
    if (!formData.startDate || !formData.endDate) return toast.error('PLEASE SELECT VALID DATES');
    if (new Date(formData.startDate) > new Date(formData.endDate)) {
      return toast.error('START DATE CANNOT BE AFTER END DATE');
    }

    let activeDaysCount = 0;
    for (const day of DAYS) {
      const conf = formData.weeklyTemplate[day];
      if (conf && conf.isActive) {
        activeDaysCount++;
        if (!conf.shifts || conf.shifts.length === 0) {
          return toast.error(`Please add at least one shift timeline row for ${day}`);
        }
        for (let i = 0; i < conf.shifts.length; i++) {
          const shift = conf.shifts[i];
          if (!shift.startTime || !shift.endTime) {
            return toast.error(`Please provide complete Start & End times on ${day} (Shift #${i + 1})`);
          }
          if (!shift.slotDuration || Number(shift.slotDuration) <= 0) {
            return toast.error(`Please provide a valid Slot Duration for ${day} (Shift #${i + 1})`);
          }
        }
      }
    }

    if (activeDaysCount === 0) {
      return toast.error('PLEASE ACTIVATE AT LEAST ONE OPERATIONS DAY');
    }

    // Client-side overlapping check
    const newStart = formData.startDate;
    const newEnd = formData.endDate;

    const overlap = schedules.find((item) => {
      if (editingId && item._id === editingId) return false;
      if (item.doctorId !== formData.doctorId) return false;

      const existStart = item.startDate ? item.startDate.split('T')[0] : '';
      const existEnd = item.endDate ? item.endDate.split('T')[0] : '';

      return existStart <= newEnd && existEnd >= newStart;
    });

    if (overlap) {
      const formattedExistStart = formatDisplayDate(overlap.startDate);
      const formattedExistEnd = formatDisplayDate(overlap.endDate);
      return toast.error(`SCHEDULE ALREADY EXISTS FOR THIS DOCTOR BETWEEN ${formattedExistStart} AND ${formattedExistEnd}`);
    }

    setLoading(true);
    try {
      const payloadData = {
        ...formData,
        weeklyTemplate: Object.keys(formData.weeklyTemplate).reduce((acc, day) => {
          const dayConfig = formData.weeklyTemplate[day];
          acc[day] = {
            ...dayConfig,
            shifts: (dayConfig.shifts || []).map((s) => ({
              ...s,
              startTime: formatTo12Hour(s.startTime),
              endTime: formatTo12Hour(s.endTime),
              breakStartTime: formatTo12Hour(s.breakStartTime),
              breakEndTime: formatTo12Hour(s.breakEndTime)
            }))
          };
          return acc;
        }, {})
      };

      const res = editingId
        ? await api.put(`/doctor-schedules/update/${editingId}`, payloadData)
        : await api.post(`/doctor-schedules/create`, payloadData);

      if (res.data.success) {
        toast.success('DOCTOR SCHEDULE SAVED SUCCESSFULLY');
        setEditingId(null);
        setFormData(getInitialFormData());
        setShowForm(false);
        fetchSchedules();
      }
    } catch (err) {
      const backendError = err.response?.data?.message || 'SAVE FAILS: CHECK INPUT DATA PARAMS';
      toast.error(backendError);
    } finally {
      setLoading(false);
    }
  };

const filteredSchedules = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();

    return schedules
      .filter((item) => {
        if (!q) return true;

        // Clean out doctor prefixes (Dr., Prof.) like the form dropdown
        const cleanName = (item.doctorName || '')
          .replace(/^(dr\.|dr|prof\.|prof)\s+/i, '')
          .trim()
          .toLowerCase();

        // Match strictly by prefix (A, B, C...) matching the form filter logic
        return cleanName.startsWith(q);
      })
      .sort((a, b) => {
        // Sort ID-wise descending (e.g., 41, 40, 39) — latest saved on top
        const idA = Number(a.scheduleId) || 0;
        const idB = Number(b.scheduleId) || 0;
        if (idA !== idB) return idB - idA;
        return String(b._id || '').localeCompare(String(a._id || ''));
      });
  }, [schedules, searchTerm]);

  const paginatedSchedules = filteredSchedules.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  /* SEARCHABLE DOCTOR DROPDOWN */
const SearchableDoctorSelect = ({ label, doctors, value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');

  // 1. Sort doctors A-Z alphabetically
  const sortedDoctors = useMemo(() => {
    return doctors
      .filter((d) => d.status === 'Active')
      .sort((a, b) => (a.doctorName || '').localeCompare(b.doctorName || ''));
  }, [doctors]);


  const filteredDoctors = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return sortedDoctors;
    return sortedDoctors.filter((doc) =>
      (doc.doctorName || '').toLowerCase().startsWith(q)
    );
  }, [sortedDoctors, query]);

  // 3. Keep text input in sync with selected value
  useEffect(() => {
    const selected = doctors.find((d) => d.doctorId === value);
    setQuery(selected ? selected.doctorName : '');
  }, [value, doctors]);

  return (
    <div className="flex flex-col flex-1 relative">
      <label className={`${masterInputLabel} text-[10px] mb-0.5 text-emerald-600`}>
        {label}
      </label>
      <div className="relative">
        <input
          type="text"
          placeholder="SEARCH PHYSICIAN..."
          value={query}
          onFocus={() => setIsOpen(true)}
          onChange={(e) => {
            setQuery(e.target.value);
            setIsOpen(true);
            if (!e.target.value) {
              onChange({ target: { name: 'doctorId', value: '' } });
            }
          }}
          className={`${masterInput} h-7 text-[11px] py-0 px-2 uppercase font-semibold cursor-pointer`}
          required={!value}
        />

        {isOpen && (
          <>
            <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} />
            <ul className="absolute left-0 right-0 top-full mt-1 max-h-48 overflow-y-auto bg-white border border-slate-300 rounded-md shadow-lg z-20 divide-y divide-slate-100">
              {filteredDoctors.length > 0 ? (
                filteredDoctors.map((doc) => (
                  <li
                    key={doc._id || doc.doctorId}
                    onClick={() => {
                      onChange({ target: { name: 'doctorId', value: doc.doctorId } });
                      setQuery(doc.doctorName);
                      setIsOpen(false);
                    }}
                    className={`px-3 py-1.5 text-[11px] font-bold uppercase cursor-pointer hover:bg-emerald-50 hover:text-emerald-700 transition-colors ${
                      doc.doctorId === value ? 'bg-emerald-100 text-emerald-800' : 'text-slate-700'
                    }`}
                  >
                    {doc.doctorName}
                  </li>
                ))
              ) : (
                <li className="px-3 py-2 text-[10px] font-bold text-slate-400 uppercase italic">
                  NO PHYSICIAN FOUND
                </li>
              )}
            </ul>
          </>
        )}
      </div>
    </div>
  );
};
  return (
    <div className={`${masterPageShell} h-screen flex flex-col overflow-hidden`}>
      <header className={`${masterHeader} flex-shrink-0`}>
        <div className={masterHeaderCard}>
          <div className="flex items-center gap-4">
            <div className={masterHeaderIcon}>
              <CalendarDays size={20} />
            </div>
            <div>
              <h1 className={`${masterTitle} text-sm font-bold`}>Doctor Schedule Template</h1>
              <p className="text-[10px] text-slate-500 font-medium">Configure Weekly Routine Rotation Loop Period</p>
            </div>
          </div>
          <button type="button" onClick={handleToggleFormView} className={`${showForm ? masterPrimaryButton : masterAccentButton} scale-95`}>
            {showForm ? <ArrowLeft size={12} /> : <Plus size={12} />}
            {showForm ? 'VIEW REGISTER LIST' : 'CREATE NEW ENGINE'}
          </button>
        </div>
      </header>

      <main className="flex-1 min-h-0 flex flex-col overflow-hidden p-1">
          {!showForm ? (
          <div className="max-w-7xl mx-auto h-full min-h-0 flex flex-col bg-white rounded-xl border border-slate-400 overflow-hidden shadow-sm">
          <div className=" border-b border-slate-200 flex-shrink-0">
          <SearchBar 
            value={searchTerm} 
            onChange={(val) => {
              setSearchTerm(val);
              setCurrentPage(1);
            }} 
            placeholder="Search by Doctor Name..." 
            totalItems={filteredSchedules.length} 
            currentPage={currentPage} 
            pageSize={pageSize} 
            onPageChange={setCurrentPage} 
            onPageSizeChange={setPageSize} 
          />
        </div>
            <div className="flex-1 min-h-0 overflow-y-auto">
          <table className="w-full min-w-[800px] table-fixed border-collapse">
              <thead>
                <tr className={masterStickyHeadRow}>
                  <th className={`${masterTableData} w-[50px] text-center text-[10px]`}>ID</th>
                  <th className={`${masterTableData} w-[150px] text-[10px]`}>Doctor Name</th>
                  <th className={`${masterTableData} w-[160px] text-[10px]`}>Date Scale Range</th>
                  <th className={`${masterTableData} text-[10px]`}>Configured Weekly Slots Routine Matrix</th>
                  <th className={`${masterTableData} w-[80px] text-center text-[10px]`}>Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 font-semibold text-slate-600">
                {paginatedSchedules.map((item) => (
                  <tr key={item._id} className="text-[11px] text-slate-700 hover:bg-emerald-50/20 transition-colors">
                    <td className={`${masterTableID} text-center`}>{item.scheduleId || '-'}</td>
                    <td className={masterTableData}>{item.doctorName}</td>
                    <td className={masterTableData}>{formatDisplayDate(item.startDate)} - {formatDisplayDate(item.endDate)}</td>
                    <td className={masterTableData}>
                      <div className="flex flex-wrap gap-1">
                        {DAYS.map((day) => {
                          const conf = item.weeklyTemplate?.[day];
                          if (!conf?.isActive || !conf.shifts.length) return null;
                          return (
                            <div key={day} className="bg-slate-100 text-[9px] px-1 py-0.5 rounded border border-slate-300 flex items-center gap-1">
                              <span className="text-emerald-600 font-extrabold">{day}:</span>
                              <span>{conf.shifts.map(s => `${s.startTime}-${s.endTime}`).join(', ')}</span>
                            </div>
                          );
                        })}
                      </div>
                    </td>
                    <td className={masterTableData}>
                      <div className="flex justify-center gap-2">
                        <button onClick={() => handleEdit(item)} className="text-emerald-600 hover:bg-emerald-100 p-1 rounded transition-colors" title="Edit Schedule">
                          <Edit size={12} />
                        </button>
                        <button onClick={() => handleDelete(item)} className="text-rose-600 hover:bg-rose-100 p-1 rounded transition-colors" title="Delete Entire Schedule">
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="h-full flex flex-col justify-between space-y-2 overflow-hidden">
            
            {/* Top Meta Input Box Settings */}
            <div className={`${masterPanelPadding} flex-shrink-0 bg-white border border-slate-300 rounded-xl py-2`}>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <SearchableDoctorSelect
                  label="Select Physician"
                  doctors={doctors}
                  value={formData.doctorId}
                  onChange={handleBaseInputChange}
                />
                <InputField label="Rotation Active From" name="startDate" value={formData.startDate} onChange={handleBaseInputChange} type="date" />
                <InputField label="Rotation Active Until" name="endDate" value={formData.endDate} onChange={handleBaseInputChange} type="date" />
              </div>
            </div>

            {/* Matrix Form Block Layout Container */}
            <div className="flex-1 bg-white border border-slate-300 rounded-xl overflow-hidden flex flex-col shadow-sm">
              
              {/* Table Single Header Element row format */}
              <div className="grid grid-cols-12 gap-1 bg-slate-100 p-2 text-[9px] font-black uppercase text-slate-500 tracking-wider text-center border-b border-slate-300 flex-shrink-0">
                <div className="col-span-2 text-left pl-2">Day Status</div>
                <div className="col-span-2">Start Time</div>
                <div className="col-span-2">End Time</div>
                <div className="col-span-1">Slot (Min)</div>
                <div className="col-span-1.5">Break Start</div>
                <div className="col-span-1.5">Break End</div>
                <div className="col-span-1.5">Booking Mode</div>
                <div className="col-span-0.5">Max Patients</div>
                <div className="col-span-0.5">Action</div>
              </div>

              {/* Grid Body List Area */}
              <div className="flex-1 overflow-y-auto divide-y divide-slate-200">
                {DAYS.map((day) => {
                  const dayConfig = formData.weeklyTemplate[day] || { isActive: false, shifts: [] };
                  
                  if (!dayConfig.isActive) {
                    return (
                      <div key={day} className="grid grid-cols-12 gap-1 items-center p-1 text-center min-h-[36px] bg-white">
                        <div className="col-span-2 flex items-center gap-2 text-left pl-2">
                          <button type="button" onClick={() => toggleDayActive(day)} className="focus:outline-none flex-shrink-0">
                            <XCircle size={16} className="text-slate-400 fill-slate-50" />
                          </button>
                          <span className="text-[11px] font-extrabold tracking-wide uppercase text-slate-400">
                            {day}
                          </span>
                        </div>
                        <div className="col-span-10 text-left text-[10px] text-slate-400 font-bold uppercase pl-2 italic">
                          Closed / Holiday Routine Row
                        </div>
                      </div>
                    );
                  }

                  return dayConfig.shifts.map((shift, idx) => (
                    <div 
                      key={`${day}-shift-${idx}`} 
                      className={`grid grid-cols-12 gap-1 items-center p-1 text-center min-h-[36px] ${
                        idx === 0 ? 'bg-emerald-50/10' : 'bg-slate-50/60 border-t border-dashed border-slate-200'
                      }`}
                    >
                      {/* Day Label & Add Controls Column */}
                      <div className="col-span-2 flex items-center gap-2 text-left pl-2">
                        {idx === 0 ? (
                          <>
                            <button type="button" onClick={() => toggleDayActive(day)} className="focus:outline-none flex-shrink-0">
                              <CheckCircle2 size={16} className="text-emerald-600 fill-emerald-50" />
                            </button>
                            <span className="text-[11px] font-extrabold tracking-wide uppercase text-emerald-700 w-8">
                              {day}
                            </span>
                            <button type="button" onClick={() => addShiftToDay(day)} className="text-[8px] px-1 font-bold rounded border border-emerald-400 text-emerald-600 bg-white hover:bg-emerald-50 transition-colors">
                              + SHIFT
                            </button>
                          </>
                        ) : (
                          <span className="text-[9px] font-extrabold text-slate-400 uppercase pl-6">
                            Shift - {idx + 1}
                          </span>
                        )}
                      </div>

                      {/* Wide time selection fields */}
                      <div className="col-span-2 px-1">
                        <input type="time" value={shift.startTime || ''} onChange={(e) => handleShiftFieldChange(day, idx, 'startTime', e.target.value)} className="w-full text-center h-7 text-[11px] border border-slate-300 rounded font-semibold bg-white px-2 min-w-[90px]" required />
                      </div>
                      <div className="col-span-2 px-1">
                        <input type="time" value={shift.endTime || ''} onChange={(e) => handleShiftFieldChange(day, idx, 'endTime', e.target.value)} className="w-full text-center h-7 text-[11px] border border-slate-300 rounded font-semibold bg-white px-2 min-w-[90px]" required />
                      </div>
                      <div className="col-span-1">
                        <input type="number" value={shift.slotDuration || ''} onChange={(e) => handleShiftFieldChange(day, idx, 'slotDuration', e.target.value)} className="w-full text-center h-7 text-[11px] border border-slate-300 rounded font-semibold bg-white" required />
                      </div>
                      <div className="col-span-1.5">
                        <input type="time" value={shift.breakStartTime || ''} onChange={(e) => handleShiftFieldChange(day, idx, 'breakStartTime', e.target.value)} className="w-full text-center h-7 text-[11px] border border-slate-300 rounded font-semibold bg-white px-1 min-w-[80px]" />
                      </div>
                      <div className="col-span-1.5">
                        <input type="time" value={shift.breakEndTime || ''} onChange={(e) => handleShiftFieldChange(day, idx, 'breakEndTime', e.target.value)} className="w-full text-center h-7 text-[11px] border border-slate-300 rounded font-semibold bg-white px-1 min-w-[80px]" />
                      </div>
                      <div className="col-span-1.5">
                        <select value={shift.bookingMode || 'Slot Based'} onChange={(e) => handleShiftFieldChange(day, idx, 'bookingMode', e.target.value)} className="w-full text-[10px] h-7 border border-slate-300 rounded font-bold bg-white px-0.5">
                          {['Slot Based', 'FCFS', 'Tele'].map(m => <option key={m} value={m === 'Tele' ? 'Tele Consultation' : m}>{m}</option>)}
                        </select>
                      </div>

                     {/* Max Patients Column */}
                    <div className="col-span-0.5 flex items-center justify-center px-1 text-center">
                      <span className="text-[10px] font-black text-emerald-600 bg-slate-100 px-1.5 py-0.5 border border-slate-300 rounded cursor-default">
                        {shift.maxPatientsPerDay || '0'}
                      </span>
                    </div>

                    {/* Action Column */}
                    <div className="col-span-0.5 flex items-center justify-center px-1 text-center">
                      {idx > 0 ? (
                        <button 
                          type="button" 
                          onClick={() => removeShiftFromDay(day, idx)} 
                          className="text-rose-500 hover:text-rose-700 p-0.5 hover:bg-rose-50 rounded transition-colors flex-shrink-0"
                          title="Remove Shift"
                        >
                          <Trash2 size={13} />
                        </button>
                      ) : (
                        <div className="w-4 h-4" />
                      )}
                    </div>

                    </div>
                  ));
                })}
              </div>
            </div>

            {/* Bottom Actions Row */}
            <div className="flex justify-end pt-1 flex-shrink-0">
              <button type="submit" disabled={loading} className={`${masterAccentButton} w-64 justify-center py-1.5 h-9`}>
                {loading ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />}
                {editingId ? 'UPDATE LOOP MATRIX' : 'SAVE ROTATION ENGINE'}
              </button>
            </div>
          </form>
        )}
      </main>
    </div>
  );
};

/* COMPACT FORMLINE HELPERS */
const InputField = ({ label, ...props }) => (
  <div className="flex flex-col flex-1">
    <label className={`${masterInputLabel} text-[10px] mb-0.5 text-emerald-600`}>{label}</label>
    <input required {...props} className={`${masterInput} h-7 text-[11px] py-0 px-2`} />
  </div>
);

const SelectField = ({ label, children, ...props }) => (
  <div className="flex flex-col flex-1">
    <label className={`${masterInputLabel} text-[10px] mb-0.5 text-emerald-600`}>{label}</label>
    <select required {...props} className={`${masterSelect} h-7 text-[11px] py-0 px-2`}>{children}</select>
  </div>
);

export default DoctoreSchedule;